/* #146.1 - Design-truth 9744:147754 / 3779:53553: the PDP recommendations header is a
   two-tab bar ("You May Also Like" / "Recently Viewed"), and BOTH tabs drive the one
   carousel global.js already builds - switching swaps the track's slides in place rather
   than standing up a second Swiper and a second lazy-fetch.

   The <product-recommendations> host replaces its own innerHTML when the recommendations
   payload lands, so the click listener is delegated off the host (which survives) and a
   MutationObserver re-applies tab state once the new subtree is in place. Swiper also
   writes to that subtree (slide classes, inline transforms), so the observer is gated on
   an explicit `rendered` marker rather than on comparing markup - comparing markup makes
   Swiper's own writes look like a stale track and loops. */
(function () {
	const STORAGE_KEY = "__sf_theme_recently";
	const HOST_SELECTOR = "product-recommendations:not(.complementary-products)";

	const readHistory = (excludeId, expirationDays) => {
		let entries = [];
		try {
			entries = JSON.parse(localStorage.getItem(STORAGE_KEY)) || [];
		} catch (error) {
			return [];
		}
		if (!Array.isArray(entries)) return [];

		const lifetime = (Number(expirationDays) || 30) * 24 * 60 * 60 * 1000;
		const now = Date.now();

		return entries
			.filter((entry) => entry && entry.productId)
			.filter((entry) => String(entry.productId) !== String(excludeId))
			.filter((entry) => now - entry.timestamp < lifetime);
	};

	const initHost = (host) => {
		if (!host || host.dataset.tabsBound === "true") return;
		host.dataset.tabsBound = "true";

		// slides caches each tab's slide markup so a repeat switch is instant and never
		// re-fetches. rendered records which set the live track currently holds.
		const state = { active: "recommendations", rendered: null, slides: {}, revealed: false };
		let observer = null;

		const trackOf = () =>
			host.querySelector(".js-recommendation-swiper .swiper-wrapper");
		const tabOf = (name) =>
			host.querySelector('[data-recommendations-tab="' + name + '"]');

		const captureRecommendations = () => {
			if (state.slides.recommendations) return;
			const track = trackOf();
			if (!track || !track.querySelector(".swiper-slide")) return;
			state.slides.recommendations = track.innerHTML;
			state.rendered = "recommendations";
		};

		const renderActive = () => {
			const track = trackOf();
			if (!track) return;
			if (state.rendered === state.active) return;

			const markup = state.slides[state.active];
			if (!markup) return;

			// Pause the observer across our own write so Swiper's follow-up DOM edits are
			// not mistaken for a tab change.
			if (observer) observer.disconnect();
			track.innerHTML = markup;
			state.rendered = state.active;

			const carousel = host.querySelector(".js-recommendation-swiper");
			if (carousel && carousel.swiper) {
				carousel.swiper.slideTo(0, 0);
				carousel.swiper.update();
			}
			if (observer) observer.observe(host, { childList: true, subtree: true });
		};

		const applyTabState = () => {
			host.querySelectorAll("[data-recommendations-tab]").forEach((tab) => {
				tab.setAttribute(
					"aria-selected",
					tab.dataset.recommendationsTab === state.active ? "true" : "false"
				);
			});
			// A4: the Recently Viewed tab stays out of the layout and the a11y tree until the
			// browsing-history fetch has actually resolved to products. The tab button is
			// wrapped in its <h2> heading (todo 5357994), so hide the heading - hiding only
			// the button would leave an empty h2 in the layout and the a11y tree.
			const recentTab = tabOf("recently-viewed");
			if (recentTab) {
				const target =
					recentTab.closest(".product-recommendations__tab-heading") || recentTab;
				target.hidden = !state.revealed;
			}
		};

		const fetchRecentlyViewed = () => {
			const tab = tabOf("recently-viewed");
			if (!tab) return Promise.resolve(false);

			const history = readHistory(
				tab.dataset.recentlyViewedExclude,
				tab.dataset.recentlyViewedExpirationDays
			);
			if (history.length === 0) return Promise.resolve(false);

			const limit = Number(tab.dataset.recentlyViewedLimit) || 6;
			const query = history
				.slice(0, limit)
				.map((entry) => "id:" + entry.productId)
				.join(" OR ");

			return fetch(
				tab.dataset.recentlyViewedUrl + "&q=" + encodeURIComponent(query)
			)
				.then((response) => response.text())
				.then((text) => {
					const parsed = new DOMParser().parseFromString(text, "text/html");
					const cards = parsed.querySelectorAll(
						"#products-grid > .collection-product-card"
					);
					if (cards.length === 0) return false;

					state.slides["recently-viewed"] = Array.from(cards)
						.map((card) => {
							card.classList.add("swiper-slide", "product-recommendations__item");
							return card.outerHTML;
						})
						.join("");
					return true;
				})
				.catch(() => false);
		};

		host.addEventListener("click", (event) => {
			const tab = event.target.closest("[data-recommendations-tab]");
			if (!tab || !host.contains(tab)) return;

			const name = tab.dataset.recommendationsTab;
			if (name === state.active) return;

			captureRecommendations();
			if (!state.slides[name]) return;

			state.active = name;
			applyTabState();
			renderActive();
		});

		observer = new MutationObserver(() => {
			captureRecommendations();
			applyTabState();
			renderActive();
		});
		observer.observe(host, { childList: true, subtree: true });

		captureRecommendations();
		applyTabState();

		fetchRecentlyViewed().then((hasProducts) => {
			state.revealed = hasProducts;
			applyTabState();
		});
	};

	const initAll = () => document.querySelectorAll(HOST_SELECTOR).forEach(initHost);

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initAll);
	} else {
		initAll();
	}
	document.addEventListener("shopify:section:load", initAll);
})();
